import React, { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, FileDown, FileUp } from "lucide-react";

/**
 * Reusable CSV importer.
 *
 * Props:
 *   endpoint       - POST URL (e.g. "/customers/import")
 *   templateUrl    - GET URL for CSV template (e.g. "/customers/import/template")
 *   templateName   - filename for template download
 *   title          - dialog title
 *   onImported     - callback fired after successful import
 *   testidPrefix   - prefix for data-testid
 *   columns        - description of expected CSV columns (rendered as hint)
 */
export default function CsvImport({
  endpoint,
  templateUrl,
  templateName = "sablon.csv",
  title = "CSV İçe Aktar",
  onImported,
  testidPrefix = "csv-import",
  columns = [],
}) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const reset = () => { setFile(null); setResult(null); if (inputRef.current) inputRef.current.value = ""; };

  const downloadTemplate = async () => {
    try {
      const res = await api.get(templateUrl, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = templateName; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const submit = async () => {
    if (!file) { toast.error("Lütfen bir CSV dosyası seçin"); return; }
    setBusy(true); setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      const msg = `${data.created} eklendi · ${data.updated} güncellendi · ${data.skipped} atlandı`;
      if (data.skipped > 0) toast.warning(msg); else toast.success(msg);
      onImported?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        className="rounded-none border-2 border-[#09090B] hover:bg-[#09090B] hover:text-white"
        onClick={() => { reset(); setOpen(true); }}
        data-testid={`${testidPrefix}-open-btn`}
      >
        <Upload size={16} className="mr-2" /> İçe Aktar
      </Button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="rounded-none border-2 border-[#09090B] bg-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">{title}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="border-2 border-dashed border-[#09090B] p-4">
              <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-2">1 · Şablon</div>
              <p className="text-sm mb-3">Doğru sütunları kullandığınızdan emin olmak için şablonu indirin.</p>
              {columns.length > 0 && (
                <div className="text-xs font-mono text-neutral-600 mb-3">
                  Sütunlar: <span className="text-[#09090B]">{columns.join(", ")}</span>
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                className="rounded-none border-2 border-[#09090B]"
                onClick={downloadTemplate}
                data-testid={`${testidPrefix}-template-btn`}
              >
                <FileDown size={14} className="mr-2" /> Şablonu İndir
              </Button>
            </div>

            <div className="border-2 border-dashed border-[#09090B] p-4">
              <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-2">2 · Dosya</div>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
                className="block w-full text-sm font-mono file:mr-3 file:py-2 file:px-3 file:border-2 file:border-[#09090B] file:bg-white file:text-[#09090B] file:font-bold file:uppercase file:text-xs file:tracking-widest hover:file:bg-[#09090B] hover:file:text-white"
                data-testid={`${testidPrefix}-file-input`}
              />
              {file && (
                <div className="mt-2 text-xs font-mono text-neutral-600">
                  Seçilen: <span className="text-[#09090B] font-bold">{file.name}</span> ({(file.size / 1024).toFixed(1)} KB)
                </div>
              )}
            </div>

            {result && (
              <div className="border-2 border-[#09090B] p-4" data-testid={`${testidPrefix}-result`}>
                <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-2">Sonuç</div>
                <div className="grid grid-cols-3 gap-2 font-mono text-sm">
                  <div className="border-l-4 border-l-[#00A650] pl-2"><div className="text-[10px] uppercase text-neutral-500">Eklendi</div><div className="text-xl font-bold">{result.created}</div></div>
                  <div className="border-l-4 border-l-[#0055FF] pl-2"><div className="text-[10px] uppercase text-neutral-500">Güncellendi</div><div className="text-xl font-bold">{result.updated}</div></div>
                  <div className="border-l-4 border-l-[#FF3B30] pl-2"><div className="text-[10px] uppercase text-neutral-500">Atlandı</div><div className="text-xl font-bold">{result.skipped}</div></div>
                </div>
                {result.errors && result.errors.length > 0 && (
                  <div className="mt-3 max-h-40 overflow-auto border-t-2 border-[#09090B] pt-2 text-xs font-mono space-y-1">
                    {result.errors.map((er, i) => (
                      <div key={i} className="text-[#FF3B30]">Satır {er.row}: {er.error}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-none border-2 border-[#09090B]"
              onClick={() => { setOpen(false); reset(); }}
            >
              Kapat
            </Button>
            <Button
              className="rounded-none bg-[#09090B] hover:bg-[#0055FF]"
              onClick={submit}
              disabled={!file || busy}
              data-testid={`${testidPrefix}-submit-btn`}
            >
              <FileUp size={16} className="mr-2" />
              {busy ? "Yükleniyor…" : "İçe Aktar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
